import { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import './ThemeSwitcher.css';

/**
 * ThemeSwitcher - dropdown in the header for picking a colour theme.
 * Compact, accessible, keyboard-navigable.
 */
export default function ThemeSwitcher() {
  const { theme, setTheme, themes } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = themes.find((t) => t.id === theme) || themes[0];

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <div className="theme-switcher" ref={ref}>
      <button
        className="theme-switcher-trigger"
        onClick={() => setOpen(!open)}
        aria-label={`Theme: ${current.label}`}
        aria-expanded={open}
        title="Change theme"
      >
        <span className="theme-switcher-icon">{current.icon}</span>
      </button>

      {open && (
        <div className="theme-switcher-dropdown" role="listbox" aria-label="Select theme">
          {themes.map((t) => (
            <button
              key={t.id}
              role="option"
              aria-selected={t.id === theme}
              className={`theme-option ${t.id === theme ? 'active' : ''}`}
              onClick={() => { setTheme(t.id); setOpen(false); }}
            >
              <span className="theme-option-icon">{t.icon}</span>
              <span className="theme-option-label">{t.label}</span>
              {t.id === theme && (
                <svg className="theme-option-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
